// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The transport: a step sequencer driven by the state of the game (M3-28, 07 §5).
//
// **A frame loop cannot play a rhythm.** M3-25 learned this on the spinner and the lesson is
// larger here: sixteen simulation steps arrive at one instant, a frame is sixteen milliseconds
// wide and not evenly spaced, and a sixteenth note at 118 BPM is 127 ms. Playing notes when the
// frame that noticed them runs would quantise every one of them to the frame grid — audible on
// a spinner, and on a drum part it is simply not music.
//
// So the sequencer keeps its own clock. One epoch, set when playback starts, and everything is
// **scheduled ahead** with `source.start(when)` against the audio context's own time. `update`
// is called once a frame and does nothing except fill the next quarter of a second; how often
// it is called, and how unevenly, changes nothing a listener can hear. That is the same
// structure as `ClickClock` and for the same reason — but where the spinner re-anchors when the
// driver stalls, the transport must never do that: a bar line that moves is worse than a bar
// line that is slightly late.
//
// **Layers change on the bar.** 07 §5: "Transitions are quantised to the bar so nothing
// lurches." The state of the game is read continuously and acted on at the next bar line, which
// is why a Star Core awarded mid-bar brings the arp in a beat or two later — deliberately.
//
// Not routed through `VoicePool`. The pool exists to bound *effect* polyphony under multiball,
// where thirty coils can fire in a second and something has to be dropped; music is a fixed,
// known number of notes per bar, and a sequencer whose hat gets stolen by a bumper is a
// sequencer with a fault.

import { Rng } from '@core/rng/Rng'
import type { Samples } from './dsp'
import { noiseFrom } from './solenoids'
import { AUDIO_SEED } from './soundBank'
import {
  DEFAULT_PROGRAM,
  LAYER_NAMES,
  MUSIC_PROGRAMS,
  renderArpNote,
  renderBassNote,
  renderClosedHat,
  renderKick,
  renderLeadNote,
  renderPadChord,
  renderSnapSnare,
  type LayerName,
  type Score,
  type Step,
} from './music'
import {
  decibelsToGain,
  type AudioBufferLike,
  type AudioNodeLike,
  type AudioPlaybackContext,
  type BiquadFilterNodeLike,
  type GainNodeLike,
} from './webAudio'

/** What the sequencer needs to know about the game. Everything else is its own business. */
export interface MusicState {
  /** A ball is in play. False in attract, between balls, and while the machine is tilted. */
  readonly playing: boolean
  /** Nobody is playing. 07 §5's "slower, sparser variant — the sound of a machine waiting". */
  readonly attract: boolean
  readonly starCore: number
  readonly multiball: boolean
  readonly supernova: boolean
  /**
   * The mode that is running, or ''.
   *
   * The sequencer watches it rather than being told when a mode starts, because a mode start is
   * not an event the rule engine raises — it is a state the engine holds, and a host that had
   * to notice the transition itself would be a second place the same thing is worked out.
   */
  readonly mode: string
}

/** Star Core at which each layer enters (07 §5). */
export const LAYER_ENTRY: Readonly<Record<LayerName, number>> = {
  bass: 0,
  arp: 1,
  drums: 10,
  pad: 20,
  // The lead is not on the Star Core ladder at all: multiball and SUPERNOVA only.
  lead: Number.POSITIVE_INFINITY,
}

/** Level of each layer, dB. */
export const LAYER_DB: Readonly<Record<LayerName, number>> = {
  bass: -11,
  arp: -17,
  drums: -13,
  pad: -19,
  lead: -16,
}

/** How far ahead notes are scheduled. Long enough to survive a dropped frame, short enough that
 *  a layer change is not already committed when the bar arrives. */
export const LOOKAHEAD_SECONDS = 0.25

/** Sixteenths in a bar. Four four, and this score has no reason to be anything else. */
export const STEPS_PER_BAR = 16

/** How long a mode intro drops everything to the pad (07 §5). */
export const INTRO_SECONDS = 2.2

/** How long BIG BANG cuts everything (07 §5). */
export const BLACKOUT_SECONDS = 0.4

/** Attract runs slower and drops everything but the bass and the pad. */
export const ATTRACT_TEMPO = 0.78

/** How far the pad ducks under a kick, and how long it takes to come back. */
export const SIDECHAIN_DEPTH = 0.45
export const SIDECHAIN_RELEASE = 0.16

/** The arp's filter, as Star Core rises: 07 §5's "resonance rising with Star Core". */
export const ARP_CUTOFF_BASE_HZ = 900
export const ARP_CUTOFF_TOP_HZ = 4200
export const ARP_Q_BASE = 1.2
export const ARP_Q_TOP = 7
/** Star Core at which the arp's filter is fully open. Beyond it, nothing more happens. */
export const ARP_FULL_STAR_CORE = 30

/** The seven note buffers, and the state each layer's output nodes are in. */
type VoiceName = 'bass' | 'arp' | 'pad' | 'lead' | 'kick' | 'snare' | 'hat'

const VOICE_NAMES: readonly VoiceName[] = ['bass', 'arp', 'pad', 'lead', 'kick', 'snare', 'hat']

export class MusicSequencer {
  readonly score: Score
  /** What the machine asked for that could not be given. Reported, never silent. */
  readonly problems: string[] = []
  /** Cost of the note renders, milliseconds. All of it after load, like the speech. */
  lateRenderMs = 0

  private readonly context: AudioPlaybackContext
  private readonly buffers = new Map<VoiceName, AudioBufferLike>()
  private readonly waiting: VoiceName[] = [...VOICE_NAMES]
  private readonly now: () => number

  /** One gain per layer, so a layer can be silent without the sequencer stopping. */
  private readonly layerGain = new Map<LayerName, GainNodeLike>()
  private arpFilter: BiquadFilterNodeLike | undefined
  private padGain: GainNodeLike | undefined
  private live: { source: { onended: (() => void) | null; stop: () => void; disconnect: () => void } }[] = []

  /** Context time of step 0. The one number the whole transport is built on. */
  private epoch = 0
  private running = false
  /** The next sixteenth to schedule, counted from the epoch. */
  private cursor = 0
  /** Layers that are sounding, adopted at a bar line. */
  private layers = new Set<LayerName>()
  private introUntil = 0
  private blackoutUntil = 0
  private tempo = 1
  /** The mode that was running last frame, so a *change* can be acted on. */
  private lastMode = ''

  constructor(
    context: AudioPlaybackContext,
    program: string | undefined,
    destination: AudioNodeLike,
    now: () => number = () => 0,
  ) {
    this.context = context
    this.now = now
    const wanted = program ?? DEFAULT_PROGRAM
    const found = MUSIC_PROGRAMS[wanted]
    if (found === undefined) {
      this.problems.push(`music: no program "${wanted}" — playing ${DEFAULT_PROGRAM}`)
    }
    this.score = found ?? MUSIC_PROGRAMS[DEFAULT_PROGRAM]!

    // One chain per layer, built once and kept: the arp's filter has to survive between notes
    // or its resonance would restart on every sixteenth, which is a click rather than a sweep.
    for (const layer of LAYER_NAMES) {
      const gain = context.createGain()
      gain.gain.value = decibelsToGain(LAYER_DB[layer])
      if (layer === 'arp') {
        const filter = context.createBiquadFilter()
        filter.type = 'lowpass'
        filter.frequency.value = ARP_CUTOFF_BASE_HZ
        filter.Q.value = ARP_Q_BASE
        gain.connect(filter)
        filter.connect(destination)
        this.arpFilter = filter
      } else {
        gain.connect(destination)
      }
      if (layer === 'pad') this.padGain = gain
      this.layerGain.set(layer, gain)
    }
  }

  get pending(): number {
    return this.waiting.length
  }

  /** Render one pending note buffer. Called a frame at a time, like the cue bank's (M3-21). */
  warmOne(): VoiceName | undefined {
    const name = this.waiting[0]
    if (name === undefined) return undefined
    this.realise(name)
    return name
  }

  /** Whether the transport is running, and which bar it is on. */
  get playing(): boolean {
    return this.running
  }

  get bar(): number {
    return Math.floor(this.cursor / STEPS_PER_BAR)
  }

  /** The layers currently sounding, in the order 07 §5 lists them. */
  get sounding(): LayerName[] {
    return LAYER_NAMES.filter((layer) => this.layers.has(layer))
  }

  /**
   * A mode intro: everything but the pad drops out for 2.2 seconds, under the callout (07 §5).
   *
   * Not a fade. The point is to make room for a voice, and a bass line at half volume is still
   * competing with it — the sudden absence is what makes the callout land.
   */
  intro(seconds = INTRO_SECONDS): void {
    this.introUntil = Math.max(this.introUntil, this.context.currentTime + seconds)
  }

  /** BIG BANG: everything stops for 400 ms, then returns at the full stack (07 §5). */
  blackout(seconds = BLACKOUT_SECONDS): void {
    this.blackoutUntil = Math.max(this.blackoutUntil, this.context.currentTime + seconds)
    // Anything already scheduled inside the gap has to be taken back, or the blackout has
    // notes in it — the one thing a blackout cannot have.
    this.stopScheduled()
  }

  /** One frame. Reads the game, and fills the next quarter of a second. */
  update(state: MusicState): void {
    const wantMusic = state.playing || state.attract
    if (!wantMusic) {
      if (this.running) this.stop()
      return
    }
    if (this.pending > 0) return // nothing to play yet; the warm-up is still going

    const now = this.context.currentTime
    if (!this.running) {
      this.running = true
      // A quarter-beat of air before the first note, so the transport does not start inside
      // whatever the frame was already doing.
      this.epoch = now + 0.1
      this.cursor = 0
      this.layers = new Set()
    }

    this.tempo = state.attract ? ATTRACT_TEMPO : 1
    this.aimArp(state, now)
    this.watchMode(state)

    while (this.timeOf(this.cursor) < now + LOOKAHEAD_SECONDS) {
      if (this.cursor % STEPS_PER_BAR === 0) this.layers = this.wanted(state)
      this.scheduleStep(this.cursor, state)
      this.cursor++
    }
  }

  /** Stop, and forget where we were. The next start is a fresh bar 0. */
  stop(): void {
    this.running = false
    this.cursor = 0
    this.layers = new Set()
    this.lastMode = ''
    this.introUntil = 0
    this.blackoutUntil = 0
    this.stopScheduled()
  }

  dispose(): void {
    this.stop()
    this.arpFilter?.disconnect()
    for (const gain of this.layerGain.values()) gain.disconnect()
    this.layerGain.clear()
  }

  /**
   * A mode starting, and what the music does about it (07 §5).
   *
   * Two behaviours from one signal. The ordinary case drops everything but the pad for 2.2
   * seconds, which is what makes room for the callout that comes with it — a *fade* would leave
   * the bass line competing, and the sudden absence is the whole effect. The score's
   * `blackoutMode` gets the other one: everything cut for 400 ms and then back at the full
   * stack, which is BIG BANG.
   */
  private watchMode(state: MusicState): void {
    if (state.mode === this.lastMode) return
    this.lastMode = state.mode
    if (state.mode === '') return
    if (state.mode === this.score.blackoutMode) this.blackout()
    else this.intro()
  }

  /** Which layers 07 §5's table says should be sounding, given the state of the game. */
  private wanted(state: MusicState): Set<LayerName> {
    const out = new Set<LayerName>()
    if (state.attract) {
      // "A slower, sparser variant — the sound of a machine waiting for someone." Bass and pad,
      // and nothing with a beat in it: an empty machine playing drums is a machine in use.
      out.add('bass')
      out.add('pad')
      return out
    }
    for (const layer of LAYER_NAMES) {
      if (layer === 'lead') continue
      if (state.starCore >= LAYER_ENTRY[layer]) out.add(layer)
    }
    if (state.multiball || state.supernova) out.add('lead')
    return out
  }

  /** Context time of a sixteenth. The whole transport, in one line. */
  private timeOf(step: number): number {
    const beat = 60 / (this.score.bpm * this.tempo)
    return this.epoch + (step * beat) / 4
  }

  private scheduleStep(step: number, state: MusicState): void {
    const at = this.timeOf(step)
    if (at < this.blackoutUntil) return
    const inBar = step % STEPS_PER_BAR
    const bar = Math.floor(step / STEPS_PER_BAR)
    const chord = this.score.chords[bar % this.score.chords.length] ?? 0
    // A mode intro leaves the pad and takes everything else away, which is what makes room for
    // the callout rather than merely turning the music down under it.
    const introOnly = at < this.introUntil

    if (!introOnly && this.layers.has('bass')) {
      this.playFigure('bass', 'bass', this.score.bass, inBar, chord, at)
    }
    if (!introOnly && this.layers.has('arp')) {
      this.playFigure('arp', 'arp', this.score.arp, inBar, chord, at)
    }
    if (!introOnly && this.layers.has('lead')) {
      this.playFigure('lead', 'lead', this.score.lead, inBar, chord, at)
    }
    if (!introOnly && this.layers.has('drums')) {
      if (this.score.kick.includes(inBar)) {
        this.playNote('kick', 'drums', 1, 1, at)
        // The pad is sidechained to the kick (07 §5). Scheduled with the kick rather than
        // driven from it, because the duck has to *start* on the beat and a callback cannot.
        this.duckPad(at)
      }
      if (this.score.snare.includes(inBar)) this.playNote('snare', 'drums', 1, 0.9, at)
      if (this.score.hat.includes(inBar)) {
        this.playNote('hat', 'drums', 1, inBar % 4 === 0 ? 0.7 : 0.45, at)
      }
    }
    if (this.layers.has('pad') && inBar === 0 && this.score.padBars.includes(bar % this.score.chords.length)) {
      this.playNote('pad', 'pad', semitoneRate(chord), 1, at)
    }
    void state
  }

  private playFigure(
    voice: VoiceName,
    layer: LayerName,
    figure: readonly Step[],
    inBar: number,
    chord: number,
    at: number,
  ): void {
    for (const note of figure) {
      if (note.at !== inBar) continue
      this.playNote(voice, layer, semitoneRate(chord + note.semitones), note.gain, at)
    }
  }

  private playNote(voice: VoiceName, layer: LayerName, rate: number, gain: number, at: number): void {
    const buffer = this.buffers.get(voice)
    const destination = this.layerGain.get(layer)
    if (buffer === undefined || destination === undefined) return

    const source = this.context.createBufferSource()
    source.buffer = buffer
    source.playbackRate.value = rate
    const level = this.context.createGain()
    level.gain.value = gain
    source.connect(level)
    level.connect(destination)

    const entry = { source }
    source.onended = () => {
      source.onended = null
      source.disconnect()
      level.disconnect()
      const i = this.live.indexOf(entry)
      if (i >= 0) this.live.splice(i, 1)
    }
    source.start(at)
    this.live.push(entry)
  }

  /** Take back everything already scheduled. Used by a blackout and by stopping. */
  private stopScheduled(): void {
    for (const entry of this.live.slice()) {
      entry.source.onended = null
      entry.source.stop()
      entry.source.disconnect()
    }
    this.live.length = 0
  }

  /** The pad's duck under a kick. */
  private duckPad(at: number): void {
    const gain = this.padGain
    if (gain === undefined) return
    const level = decibelsToGain(LAYER_DB.pad)
    gain.gain.setValueAtTime(level * (1 - SIDECHAIN_DEPTH), at)
    gain.gain.setTargetAtTime(level, at, SIDECHAIN_RELEASE)
  }

  /** 07 §5's "resonance rising with Star Core". */
  private aimArp(state: MusicState, now: number): void {
    const filter = this.arpFilter
    if (filter === undefined) return
    const through = Math.min(1, Math.max(0, state.starCore / ARP_FULL_STAR_CORE))
    filter.frequency.setTargetAtTime(
      ARP_CUTOFF_BASE_HZ + (ARP_CUTOFF_TOP_HZ - ARP_CUTOFF_BASE_HZ) * through,
      now,
      0.12,
    )
    filter.Q.setTargetAtTime(ARP_Q_BASE + (ARP_Q_TOP - ARP_Q_BASE) * through, now, 0.12)
  }

  private realise(voice: VoiceName): void {
    const at = this.waiting.indexOf(voice)
    if (at >= 0) this.waiting.splice(at, 1)
    const started = this.now()
    const samples = renderVoice(voice, this.context.sampleRate)
    const buffer = this.context.createBuffer(1, samples.length, this.context.sampleRate)
    buffer.getChannelData(0).set(samples)
    this.buffers.set(voice, buffer)
    this.lateRenderMs += this.now() - started
  }
}

/** Playback rate for a note this many semitones above the buffer's own pitch. */
export function semitoneRate(semitones: number): number {
  return Math.pow(2, semitones / 12)
}

function renderVoice(voice: VoiceName, sampleRate: number): Samples {
  // Its own stream per voice, so adding a timbre never changes an existing one — the same rule
  // the cue bank and the speech bank follow.
  const noise = noiseFrom(new Rng(AUDIO_SEED ^ seedFor(voice)))
  switch (voice) {
    case 'bass':
      return renderBassNote(sampleRate)
    case 'arp':
      return renderArpNote(sampleRate)
    case 'pad':
      return renderPadChord(sampleRate)
    case 'lead':
      return renderLeadNote(sampleRate)
    case 'kick':
      return renderKick(sampleRate, noise)
    case 'snare':
      return renderSnapSnare(sampleRate, noise)
    case 'hat':
      return renderClosedHat(sampleRate, noise)
  }
}

function seedFor(name: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < name.length; i++) h = Math.imul(h ^ name.charCodeAt(i), 0x01000193)
  return h | 0
}
